import React from 'react'
import { Token } from '@/types/token.type'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { fNumber } from '@/utils/format-number'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'

type TransferType = 'user-to-user' | 'system-topup'

interface TransferFormProps {
  transferType: TransferType
  tokens: Token[]
  selectedToken: string
  inputAmount: string
  selectedPercentage: number | null
  swapLoading: boolean
  selectedFromUser: string
  selectedToUser: string
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onPercentageClick: (percentage: number) => void
  onMaxClick: () => void
  onConfirm: () => void
  getCurrentUserTokenBalance: () => number
}

export default function TransferForm({
  transferType,
  tokens,
  selectedToken,
  inputAmount,
  selectedPercentage,
  swapLoading,
  selectedFromUser,
  selectedToUser,
  onInputChange,
  onPercentageClick,
  onMaxClick,
  onConfirm,
  getCurrentUserTokenBalance,
}: TransferFormProps) {
  const getSelectedToken = () => {
    return tokens.find((t) => t.id === selectedToken)
  }

  const isTransferDisabled = () => {
    return (
      swapLoading ||
      !selectedToken ||
      !inputAmount ||
      +inputAmount === 0 ||
      (transferType === 'user-to-user' &&
        (!selectedFromUser || !selectedToUser)) ||
      (transferType === 'system-topup' && !selectedToUser)
    )
  }

  const getButtonText = () => {
    if (swapLoading) return 'กำลังดำเนินการ...'
    return transferType === 'user-to-user' ? 'โอนเหรียญ' : 'เติมเงิน'
  }

  return (
    <div className="space-y-6">
      {/* Token Balance Section */}
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <Label className="text-sm font-medium">จำนวนเหรียญ</Label>
          {transferType === 'user-to-user' &&
            selectedFromUser &&
            selectedToken && (
              <span className="text-sm text-muted-foreground">
                ยอดคงเหลือ: {fNumber(getCurrentUserTokenBalance())}
              </span>
            )}
        </div>

        {selectedToken && (
          <div className="bg-muted rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Avatar>
                <AvatarImage
                  className="object-cover"
                  src={getSelectedToken()?.logoUrl || ''}
                  alt={getSelectedToken()?.name || ''}
                />
                <AvatarFallback>
                  {getSelectedToken()?.symbol || ''}
                </AvatarFallback>
              </Avatar>

              <div>
                <div className="font-medium">
                  {getSelectedToken()?.symbol || ''}
                </div>
                <div className="text-xs text-muted-foreground">
                  {getSelectedToken()?.name || ''}
                </div>
              </div>
            </div>
            <div className="flex-1 max-w-32">
              <Input
                disabled={swapLoading}
                value={inputAmount}
                onChange={onInputChange}
                placeholder="ใส่จำนวน"
                className="text-right bg-transparent border-none"
              />
            </div>
          </div>
        )}

        <div className="flex gap-2">
          {[25, 50, 75].map((percentage) => (
            <Button
              key={percentage}
              disabled={swapLoading}
              variant={
                selectedPercentage === percentage ? 'default' : 'outline'
              }
              size="sm"
              onClick={() => onPercentageClick(percentage)}
            >
              {percentage}%
            </Button>
          ))}
          <Button
            variant={selectedPercentage === 100 ? 'default' : 'outline'}
            size="sm"
            onClick={onMaxClick}
          >
            สูงสุด
          </Button>
        </div>
      </div>

      {/* Transfer Button */}
      <Button
        disabled={isTransferDisabled()}
        className="w-full"
        onClick={onConfirm}
      >
        {getButtonText()}
      </Button>
    </div>
  )
}
